import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Edit, UserPlus, Mail, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { StatusBadge } from '@/components/ui/status-badge';
import { Class, Student } from '@/types';
import { classApi, studentApi } from '@/lib/api';
// import StudentAssignment from '@/components/StudentAssignment';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const ClassDetailPage = () => {
  const { classId } = useParams<{ classId: string }>();
  const [classData, setClassData] = useState<Class | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [canSendEmails, setCanSendEmails] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchClassData = async () => {
      try {
        setLoading(true);
        if (classId) {
          const data = await classApi.getById(classId);
          setClassData(data);

          // Fetch students assigned to this class
          if (data.studentIds && data.studentIds.length > 0) {
            const studentsData = await Promise.all(
              data.studentIds.map(id => studentApi.getById(id))
            );
            setStudents(studentsData);
          }
        }
      } catch (error) {
        console.error('Error fetching class data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load class data. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchClassData();
  }, [classId, toast]);

  // Check if welcome emails can be sent
  useEffect(() => {
    const checkCanSendEmails = async () => {
      if (classId) {
        try {
          const response = await classApi.canSendWelcomeEmails(classId);
          setCanSendEmails(response.canSend);
        } catch (error) {
          console.error('Error checking welcome email status:', error);
        }
      }
    };

    checkCanSendEmails();
  }, [classId]);

  // Send bulk welcome emails
  const handleSendBulkEmails = async () => {
    if (!classId) return;

    setSendingEmails(true);
    try {
      const response = await classApi.sendBulkWelcomeEmails(classId);

      if (response.success) {
        toast({
          title: 'Success',
          description: response.message,
        });
        setCanSendEmails(false); // Disable the button after successful send
      } else {
        toast({
          title: 'Error',
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error sending bulk welcome emails:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to send welcome emails. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingEmails(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/classes">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Class Details</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading class details...</p>
          </div>
        </div>
      ) : classData ? (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle>Class Information</CardTitle>
              <Button asChild>
                <Link to={`/classes/${classId}/edit`}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Class
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Name</h3>
                  <p className="text-lg">{classData.name}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Grade Level</h3>
                  <p className="text-lg">{classData.grade}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Batch ID</h3>
                  <p className="text-lg">{classData.academicYearId}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Teacher ID</h3>
                  <p className="text-lg">{classData.teacherId || 'Not assigned'}</p>
                </div>
                <div className="md:col-span-2">
                  <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
                  <p className="text-lg">{classData.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Class Management</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="students">
                <TabsList className="mb-4">
                  <TabsTrigger value="students">Students</TabsTrigger>
                  <TabsTrigger value="tests">Tests</TabsTrigger>
                </TabsList>

                <TabsContent value="students">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium">Enrolled Students</h3>
                      <div className="flex gap-2">
                        {canSendEmails && students.length > 0 && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" disabled={sendingEmails}>
                                {sendingEmails ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <Mail className="mr-2 h-4 w-4" />
                                    Send Welcome Emails
                                  </>
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Send Welcome Emails</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will send welcome emails to all {students.length} students in this class.
                                  Each student will receive an email with instructions to set up their account and access the student portal.
                                  <br /><br />
                                  <strong>Note:</strong> This action can only be performed once per class and cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleSendBulkEmails}>
                                  Send Welcome Emails
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        <Button asChild>
                          <Link to={`/classes/${classId}/students`}>
                            <UserPlus className="mr-2 h-4 w-4" />
                            Manage Students
                          </Link>
                        </Button>
                      </div>
                    </div>

                    {students.length === 0 ? (
                      <p className="text-muted-foreground">No students enrolled in this class.</p>
                    ) : (
                      <div className="border rounded-md">
                        <table className="min-w-full divide-y divide-border">
                          <thead>
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Student ID
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Name
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {students.map((student) => (
                              <tr key={student.id}>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  {student.studentId}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  {student.firstName} {student.lastName}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  {student.email}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  <StatusBadge status={student.status} variant="rounded" />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                  <Button variant="outline" size="sm" asChild>
                                    <Link to={`/students/${student.id}`}>View Profile</Link>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="tests">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium">Assigned Tests</h3>
                      <Button asChild>
                        <Link to={`/tests/new?classId=${classId}`}>
                          <UserPlus className="mr-2 h-4 w-4" />
                          Create Test
                        </Link>
                      </Button>
                    </div>

                    {classData.testIds && classData.testIds.length > 0 ? (
                      <div className="border rounded-md">
                        <table className="min-w-full divide-y divide-border">
                          <thead>
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Test ID
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Title
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {classData.testIds.map((testId) => (
                              <tr key={testId}>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  {testId}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  Test Title
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                                    draft
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                                  <Button variant="outline" size="sm" asChild>
                                    <Link to={`/tests/${testId}`}>View Test</Link>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No tests assigned to this class.</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <p className="text-muted-foreground">Class not found.</p>
              <Button className="mt-4" asChild>
                <Link to="/classes">Back to Classes</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClassDetailPage;
